import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  Trash, 
  Plus, 
  Warning,
  Info,
  FileText,
  Chat,
  BookOpen,
  MapPin,
  ShieldCheck,
  Question,
  PencilSimple,
  Check,
  CaretDown,
  DotsSixVertical,
  CircleNotch,
  Database,
  Code,
  Play,
  CheckCircle,
  XCircle,
  Lightning,
  Eye,
  EyeSlash,
  Plug,
  DotsThreeVertical,
  Clock,
  Pencil
} from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/text-area';
import { Label } from '../ui/label';

import { ConfirmModal } from '../ui/confirm-modal';
import { cn } from '../../lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Switch } from '../ui/switch';
import { getSupabaseClient } from '../../supabaseClient';
import { useBusinessType } from '../../context/BusinessContext';

import { runAutomaticConflictDetection } from '../../utils/conflictDetection';
import ApiHeadersInput from '../ApiHeadersInput';
import ApiQueryParamsInput from '../ApiQueryParamsInput';
import RichTextEditor from '../ui/RichTextEditor';
import JsonMentionTextarea from '../ui/JsonMentionTextarea';
import MentionInput from '../ui/MentionInput';
import { API_BASE_URL, endpoints } from '../../config/api';
import BrainSettingConditions from '../brain/BrainSettingConditions';
import { getUserAccountId } from '../../utils/accountUtils';

// Animation variants - optimize for mobile performance
const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1,
    transition: { duration: 0.3 }
  },
  exit: { 
    opacity: 0,
    transition: { duration: 0.2 }
  }
};

const modalVariants = {
  hidden: { scale: 0.95, opacity: 0, y: 20 },
  visible: { 
    scale: 1, 
    opacity: 1, 
    y: 0, 
    transition: { 
      type: "spring", 
      damping: 30, 
      stiffness: 300,
      duration: 0.4 
    }
  },
  exit: { 
    scale: 0.98,
    opacity: 0,
    y: 10,
    transition: { duration: 0.2 }
  }
};



export const ComplexAnswerSettingsModal = ({
  isOpen,
  onClose,
  onSave,
  answer,
  locations = [],
  isNew = false,
  isInline = false,
  isPreLoaded = false,
  hideContentTab = false, // New prop to hide content tab for settings-level editing
  preSelectedCategoryId = null,
  onUpdateSetting, // Add new prop
  onCreateCategory, // Add new prop for category creation
  newlyCreatedCategory, // Add new prop for auto-selecting newly created category
  onClearNewlyCreatedCategory, // Add new prop for clearing newly created category
  isSidePanelOpen = false, // NEW: For layout adjustment when side panels are open
  onOpenActivationConditions,
  setting
}) => {
  // State management

  // Initialize formData directly with pre-populated data if available
  const [formData, setFormData] = useState(() => {
    if (answer?.formData) {
      // Use pre-populated form data immediately - no useEffect delay!
      return answer.formData;
    }
    return {
      name: '', description: '', triggers: [], questions: [], content: '',
      category_id: preSelectedCategoryId || '', type: 'text', grouping_id: null,
      answer_type: 'knowledge', // New field for knowledge vs API-based
      api_endpoint: '',
      api_method: 'POST',
      api_headers: {},
      api_query_params: {},
      api_body_template: {},
      api_response_template: '',
      auth_type: 'none',
      auth_config: {}
    };
  });

  const [currentTrigger, setCurrentTrigger] = useState('');
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [currentQuestionType, setCurrentQuestionType] = useState('string');
  const [currentQuestionOptions, setCurrentQuestionOptions] = useState([]);
  const [newOption, setNewOption] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] = useState(false);
  const initializedRef = useRef(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const { businessType } = useBusinessType();
  const supabase = getSupabaseClient(businessType);
  const [categories, setCategories] = useState([]);

  // Location variant states (similar to SimpleAnswerModal)
  const [locationGroupings, setLocationGroupings] = useState([]);
  const [locationAssignments, setLocationAssignments] = useState({});
  const [activeLocationGrouping, setActiveLocationGrouping] = useState(null);
  const [formDataByGrouping, setFormDataByGrouping] = useState({});
  const [hasLocationChanges, setHasLocationChanges] = useState(false);

  // Response template toggle state
  const [showResponseTemplate, setShowResponseTemplate] = useState(false);

  // API Testing state
  const [isTestingAPI, setIsTestingAPI] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testInputs, setTestInputs] = useState({});
  const [showTestSection, setShowTestSection] = useState(false);

  // Password visibility states
  const [showBearerToken, setShowBearerToken] = useState(false);
  const [showBasicPassword, setShowBasicPassword] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);

  // Integration-based auth states
  const [useIntegrationAuth, setUseIntegrationAuth] = useState(false);
  const [selectedProviderId, setSelectedProviderId] = useState('');
  const [availableProviders, setAvailableProviders] = useState([]);
  const [userIntegrationsMap, setUserIntegrationsMap] = useState({});

  // Workflow step navigation state
  const [currentStep, setCurrentStep] = useState('triggers');
  const [isDescriptionOpen, setIsDescriptionOpen] = useState(false);
  const descriptionRef = useRef(null);

  const [requirements, setRequirements] = useState([]);
  const [accountId, setAccountId] = useState(null);

  const [activeTab, setActiveTab] = useState('setup');

  useEffect(() => {
    function handleClickOutside(event) {
        if (descriptionRef.current && !descriptionRef.current.contains(event.target)) {
            setIsDescriptionOpen(false);
        }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
        document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [descriptionRef]);

  // Step validation and navigation helpers
  const isSetupComplete = () => formData.name.trim().length > 0;

  const isStepCompleted = (stepId) => {
    switch (stepId) {
      case 'triggers':
        return formData.triggers && formData.triggers.length > 0;
      case 'questions':
        return true; // Questions are optional
      case 'response':
        if (formData.answer_type === 'knowledge') {
          // For knowledge, content can be empty, we just check that the selection has been made.
          return true;
        } else if (formData.answer_type === 'api') {
          return formData.api_endpoint.trim().length > 0;
        }
        return false;
      case 'test':
        return true; // Test step is always "completed" once accessible
      default:
        return false;
    }
  };

  const isStepAccessible = (stepId) => {
    if (!isSetupComplete()) return false;

    switch (stepId) {
      case 'triggers':
        return true;
      case 'questions':
        return isStepCompleted('triggers');
      case 'response':
        return isStepCompleted('triggers'); // Can configure response after triggers
      case 'test':
        return isStepCompleted('response') && formData.answer_type === 'api' && formData.questions?.length > 0;
      default:
        return false;
    }
  };

  // Safe JSON parsing function
  const safeJsonParse = (value, defaultValue = {}) => {
    if (!value || typeof value !== 'string') return value || defaultValue;
    try {
      return value.trim() === '' ? defaultValue : JSON.parse(value);
    } catch (error) {
      console.warn('Failed to parse JSON:', value, error);
      return defaultValue;
    }
  };

  // Format questions for mention components
  const questionsForMention = [
    // Add integration variables first if provider is selected
    ...(selectedProviderId ? [
      {
        id: 'integration_token',
        text: 'integration_token'
      },
      // Add credential variables from selected provider
      ...(Array.isArray(userIntegrationsMap[selectedProviderId]) ? userIntegrationsMap[selectedProviderId] : []).map(credentialType => ({
        id: credentialType,
        text: credentialType
      }))
    ] : []),
    // Then add question variables
    ...formData.questions?.map((q, index) => ({
      id: q.id, // Use the actual question ID
      text: `question_${index + 1}_response` // Use the backend format with _response suffix
    })) || []
  ];

  // Custom handler for API body template with JSON-friendly placeholders
  const handleApiBodyTemplateChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, api_body_template: value }));
    setHasUnsavedChanges(true);
  };

  // Custom mention insertion for JSON context
  const handleJsonMentionInsert = (originalValue, question, cursorPos) => {
    // Find the question index to create the correct question_N_response format
    const questionIndex = formData.questions.findIndex(q => q.id === question.id);
    if (questionIndex === -1) return originalValue;

    // Check if we're inside a JSON string context
    const beforeCursor = originalValue.substring(0, cursorPos);
    const afterCursor = originalValue.substring(cursorPos);

    // Count unescaped quotes before cursor
    const quotesBeforeCursor = (beforeCursor.match(/(?<!\\)"/g) || []).length;
    const isInsideString = quotesBeforeCursor % 2 === 1;

    // Use the correct backend format: question_N_response with double curly brackets
    const questionResponseKey = `question_${questionIndex + 1}_response`;

    let placeholderText;
    if (isInsideString) {
      // We're inside a JSON string, insert just the placeholder
      placeholderText = `{{${questionResponseKey}}}`;
    } else {
      // We're outside a string, insert as a quoted string
      placeholderText = `"{{${questionResponseKey}}}"`;
    }

    return placeholderText;
  };

  // Handle window resize to detect mobile view
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load available integrations for the user
  useEffect(() => {
    const loadData = async () => {
      if (isOpen) {
        loadAvailableIntegrations();
        const { accountId: id } = await getUserAccountId();
        setAccountId(id);
      }
    };
    loadData();
  }, [isOpen]);

  const loadAvailableIntegrations = async () => {
    try {
      const { getUserAccountId } = await import('../../utils/accountUtils');
      const { accountId } = await getUserAccountId();
      if (!accountId) {
        console.log('🔧 No authenticated user found');
        return;
      }

      // Get user's connected integrations with provider info
      const { data: integrations, error } = await supabase
        .from('accounts_integrations')
        .select(`
          id,
          integration_status,
          is_active,
          api_providers!provider_id (
            id,
            name
          )
        `)
        .eq('account_id', accountId)
        .eq('integration_status', 'complete');

      if (error) throw error;

      // Get credentials for these integrations
      const integrationIds = integrations?.map(i => i.id) || [];
      let credentialsMap = {};

      if (integrationIds.length > 0) {
        const { data: credentials, error: credError } = await supabase
          .from('api_credentials')
          .select('integration_id, credential_type')
          .in('integration_id', integrationIds);

        if (credError) throw credError;

        // Build credentials map by integration ID
        (credentials || []).forEach(cred => {
          if (!credentialsMap[cred.integration_id]) {
            credentialsMap[cred.integration_id] = [];
          }
          credentialsMap[cred.integration_id].push(cred.credential_type);
        });
      }

      // Transform to get unique providers and build credentials mapping
      const providerMap = {};
      const credentialTypesMap = {};

      (integrations || []).forEach(integration => {
        const providerId = integration.api_providers.id;
        if (!providerMap[providerId]) {
          providerMap[providerId] = integration.api_providers;
        }
        // Map credential types instead of raw metadata
        credentialTypesMap[providerId] = credentialsMap[integration.id] || [];
      });

      setAvailableProviders(Object.values(providerMap));
      setUserIntegrationsMap(credentialTypesMap);
    } catch (error) {
      console.error('Error loading integrations:', error);
    }
  };

  // Handle provider selection
  const handleIntegrationSelection = (providerId) => {
    setSelectedProviderId(providerId);

    // Update auth config with provider reference
    setFormData(prev => ({
      ...prev,
      auth_type: 'integration',
      auth_config: {
        api_provider_id: providerId,
        use_integration_auth: true
      }
    }));
    setHasUnsavedChanges(true);
  };

  // Load all details for an existing complex answer
  const loadComplexAnswerDetails = async (settingsId) => {
    try {
      // Get current user
      const { getUserAccountId } = await import('../../utils/accountUtils');
      const { accountId } = await getUserAccountId();
      if (!accountId) return;

      // Fetch settings, groupings, answers concurrently for optimal performance
      const [settingsRes, groupingsRes, answersRes, reqRes] = await Promise.all([
        supabase.from('brain_settings').select('*').eq('id', settingsId),
        supabase.from('brain_groupings').select('*').eq('settings_id', settingsId),
        supabase.from('brain_complex_answers').select('*').eq('settings_id', settingsId),
        supabase.from('brain_setting_requirements').select('*, brain_conversation_topics(topic_name)').eq('settings_id', settingsId)
      ]);

      if (settingsRes.error) throw settingsRes.error;
      if (groupingsRes.error) throw groupingsRes.error;
      if (answersRes.error) throw answersRes.error;
      if (reqRes.error) {
        console.error("Error fetching requirements", reqRes.error);
      } else {
        setRequirements(reqRes.data || []);
      }

      // Check if settings record exists
      if (!settingsRes.data || settingsRes.data.length === 0) {
        // DON'T call onClose() as it causes infinite re-mounting
        // Instead, just stop and let the modal show create form
        return;
      }

      const settingsData = settingsRes.data[0]; // Get first (and should be only) record
      const groupingsData = groupingsRes.data || [];
      const answersData = answersRes.data || [];

      // Populate location assignments
      let fetchedAssignments = {};
      if (groupingsData.length > 0) {
        const groupIds = groupingsData.map(g => g.id);
        const { data: locData, error: locErr } = await supabase
          .from('brain_grouping_locations')
          .select('grouping_id, location_id')
          .in('grouping_id', groupIds);
        if (locErr) throw locErr;
        (locData || []).forEach(row => {
          if (!fetchedAssignments[row.grouping_id]) fetchedAssignments[row.grouping_id] = [];
          fetchedAssignments[row.grouping_id].push(row.location_id);
        });
      }

      // Populate content by grouping and find first answer/content
      const groupingContentMap = {};
      let firstComplexAnswerId = null;
      let firstContent = '';
      let firstAnswerType = 'knowledge';
      let firstApiEndpoint = '';
      let firstApiMethod = 'POST';
      let firstApiHeaders = {};
      let firstApiQueryParams = {};
      let firstApiBodyTemplate = {};
      let firstApiResponseTemplate = '';
      let firstAuthType = 'none';
      let firstAuthConfig = {};
      let loadedQuestions = [];

      if (answersData.length > 0) {
        // Load API configurations for API-based answers
        const apiAnswerIds = answersData
          .filter(ans => ans.answer_type === 'api')
          .map(ans => ans.id);

        let apiConfigsMap = {};
        if (apiAnswerIds.length > 0) {
          const { data: apiConfigs, error: apiError } = await supabase
            .from('brain_complex_answers_api')
            .select('*')
            .in('complex_answer_id', apiAnswerIds);

          if (!apiError && apiConfigs) {
            apiConfigsMap = apiConfigs.reduce((acc, config) => {
              acc[config.complex_answer_id] = config;
              return acc;
            }, {});
          }
        }

        // Assuming the first grouping in the fetched list is the 'primary' one
        const firstGroupingId = groupingsData.length > 0 ? groupingsData[0].id : null;
        answersData.forEach((ans, index) => {
          const apiConfig = apiConfigsMap[ans.id] || {};

          groupingContentMap[ans.grouping_id] = { 
            content: ans.content || '',
            answer_type: ans.answer_type || 'knowledge',
            api_endpoint: apiConfig.api_endpoint || '',
            api_method: apiConfig.api_method || 'POST',
            api_headers: safeJsonParse(apiConfig.api_headers, {}),
            api_query_params: safeJsonParse(apiConfig.api_query_params, {}),
            api_body_template: safeJsonParse(apiConfig.api_body_template, {}),
            api_response_template: apiConfig.api_response_template || '',
            auth_type: apiConfig.auth_type || 'none',
            auth_config: apiConfig.auth_config || {}
          };

          if (index === 0 || ans.grouping_id === firstGroupingId) { 
            // Prioritize first grouping's answer, fallback to first answer overall
            if (!firstComplexAnswerId || ans.grouping_id === firstGroupingId) {
               firstComplexAnswerId = ans.id;
               firstContent = ans.content || '';
               firstAnswerType = ans.answer_type || 'knowledge';
               firstApiEndpoint = apiConfig.api_endpoint || '';
               firstApiMethod = apiConfig.api_method || 'POST';
               firstApiHeaders = safeJsonParse(apiConfig.api_headers, {});
               firstApiQueryParams = safeJsonParse(apiConfig.api_query_params, {});
               firstApiBodyTemplate = safeJsonParse(apiConfig.api_body_template, {});
               firstApiResponseTemplate = apiConfig.api_response_template || '';
               firstAuthType = apiConfig.auth_type || 'none';
               firstAuthConfig = apiConfig.auth_config || {};
            }
          }
        });

        // Load questions for the primary complex answer
        if (firstComplexAnswerId) {
          const { data: questionsData, error: questionsErr } = await supabase
            .from('brain_complex_answers_questions')
            .select('*')
            .eq('complex_answer_id', firstComplexAnswerId)
            .order('order');

          if (questionsErr) throw questionsErr;

          // Get enum options for all enum-type questions
          let enumOptionsMap = {};
          if (questionsData && questionsData.length > 0) {
            const questionIds = questionsData.map(q => q.id);
            const { data: enumOptions, error: enumErr } = await supabase
              .from('brain_complex_answers_questions_enum')
              .select('complex_answer_question_id, enum_value')
              .in('complex_answer_question_id', questionIds);

            if (!enumErr && enumOptions) {
              // Group enum options by question ID
              enumOptions.forEach(option => {
                if (!enumOptionsMap[option.complex_answer_question_id]) {
                  enumOptionsMap[option.complex_answer_question_id] = [];
                }
                enumOptionsMap[option.complex_answer_question_id].push(option.enum_value);
              });
            }
          }

          // Transform questions data to match form structure
          loadedQuestions = (questionsData || []).map(q => ({
            id: q.id,
            question: q.question,
            type: q.type,
            order: q.order,
            options: enumOptionsMap[q.id] || []
          }));
        }
      }

      // Update the main form state
      setFormData(prev => ({
        ...prev, // Keep existing ID if available from initial prop
        id: settingsData.id,
        category_id: settingsData.category_id,
        name: settingsData.name || '',
        description: settingsData.description || '',
        triggers: Array.isArray(settingsData.triggers) ? [...settingsData.triggers] : [],
        questions: loadedQuestions,
        content: firstContent,
        type: 'text', // complex answers are always type 'text' internally
        answer_type: firstAnswerType,
        api_endpoint: firstApiEndpoint,
        api_method: firstApiMethod,
        api_headers: safeJsonParse(firstApiHeaders, {}),
        api_query_params: safeJsonParse(firstApiQueryParams, {}),
        api_body_template: safeJsonParse(firstApiBodyTemplate, {}),
        api_response_template: firstApiResponseTemplate,
        auth_type: firstAuthType,
        auth_config: firstAuthConfig,
        grouping_id: prev.grouping_id // Preserve any existing grouping_id
      }));

      // Initialize integration auth state if needed
      if (firstAuthType === 'integration' && firstAuthConfig?.api_provider_id) {
        setUseIntegrationAuth(true);
        setSelectedProviderId(firstAuthConfig.api_provider_id);

        // Load credentials for this provider from user's integrations
        const { data: integrationData, error: integrationError } = await supabase
          .from('accounts_integrations')
          .select('id')
          .eq('account_id', accountId)
          .eq('provider_id', firstAuthConfig.api_provider_id)
          .eq('is_active', true)
          .eq('integration_status', 'complete')
          .single();

        if (!integrationError && integrationData) {
          // Get credentials for this integration
          const { data: credentials, error: credError } = await supabase
            .from('api_credentials')
            .select('credential_type')
            .eq('integration_id', integrationData.id);

          if (!credError && credentials) {
            const credentialTypes = credentials.map(c => c.credential_type);
            setUserIntegrationsMap(prev => ({ ...prev, [firstAuthConfig.api_provider_id]: credentialTypes }));
          }
        }
      }

      // Initialize location grouping states for complex answers
      if (settingsData.id) {
        const firstGroupingIdIfAny = groupingsData.length > 0 ? groupingsData[0].id : null;
        // Check for grouping_id first (for variants), then mainGroupingId (for settings), then fallback to first grouping
        const initialActiveGroupingId = settingsData.grouping_id || settingsData.mainGroupingId || firstGroupingIdIfAny;

        setLocationGroupings(groupingsData);
        setLocationAssignments(fetchedAssignments);
        setFormDataByGrouping(groupingContentMap);
        setActiveLocationGrouping(initialActiveGroupingId);
      }

    } catch (error) {
      console.error("Error loading complex answer details:", error);
      toast.error("Failed to load complex answer details.");
      // Optionally reset or close modal on error
    }
  };

  // Initialize form with answer data when opened
  useEffect(() => {
    if (isOpen) {
      // Only reset form data if NOT pre-populated
      if (!answer?.formData) {
        setFormData({
          name: '', description: '', triggers: [], questions: [], content: '',
          category_id: preSelectedCategoryId || '', type: 'text', grouping_id: null
        });
      }

      setValidationErrors({});
      setHasUnsavedChanges(false);
      setHasLocationChanges(false);

      // OPTIMIZATION: Skip loading states when data is pre-loaded
      // All data is pre-loaded, no loading states needed

      // Fetch categories always (but make it non-blocking for pre-loaded data)
      (async () => {
        try {
          const { getUserAccountId } = await import('../../utils/accountUtils');
          const { accountId } = await getUserAccountId();
          if (!accountId) return;
          const { data } = await supabase
            .from('brain_categories')
            .select('id, name').eq('account_id', accountId).order('name');
          setCategories(data || []);
        } catch (e) { console.error("Failed to fetch categories", e); }
      })();

      // OPTIMIZATION: Only load details if NOT pre-loaded and editing existing answer
      if (!isPreLoaded && answer && answer.id && !isNew) {
        // Use answer.id directly as the settings_id since it comes from the sidebar selection
        loadComplexAnswerDetails(answer.id);
      } else if (isPreLoaded && answer) {
        // OPTIMIZATION: Use pre-loaded data instantly
        if (answer.formData) {
          // Form data is already pre-populated, use it directly
          setFormData(answer.formData);
        } else if (answer.fullData) {
          // Use fullData from pre-loading
          const fullData = answer.fullData;
          if (fullData.locationGroupings) {
            setFormData({
              id: answer.id,
              category_id: answer.category_id || '',
              name: answer.name || '',
              description: answer.description || '',
              triggers: Array.isArray(answer.triggers) ? [...answer.triggers] : [],
              content: answer.content || '',
              type: 'text',
              questions: [],
              grouping_id: fullData.grouping_id // Preserve grouping_id for variants
            });
          } else if (!isNew) {
            // Fallback for existing answers without full pre-loading
            setFormData(prev => ({
              ...prev,
              id: answer.id,
              category_id: answer.category_id || '',
              name: answer.name || '',
              description: answer.description || '',
              triggers: Array.isArray(answer.triggers) ? [...answer.triggers] : [],
              content: answer.content || '',
              type: 'text',
              grouping_id: answer.grouping_id || fullData?.grouping_id // Preserve grouping_id
            }));
          }
        } else {
          // For new answers, data structure is already set up above
          if (!isNew && answer) {
            // Make sure we preserve any grouping_id passed directly in the answer object
            setFormData(prev => ({
              ...prev,
              id: answer.id,
              category_id: answer.category_id || '',
              name: answer.name || '',
              description: answer.description || '',
              triggers: Array.isArray(answer.triggers) ? [...answer.triggers] : [],
              content: answer.content || '',
              type: 'text',
              grouping_id: answer.grouping_id || null
            }));
          }
        }

        // Initialize location grouping states for complex answers
        if (answer && answer.id) {
          const fetchedGroupings = answer.locationGroupings || [];
          const fetchedAssignments = answer.locationAssignments || {};
          const fetchedFormDataByGrouping = answer.formDataByGrouping || {};

          const firstGroupingIdIfAny = fetchedGroupings.length > 0 ? fetchedGroupings[0].id : null;
          // Check for grouping_id first (for variants), then mainGroupingId (for settings), then fallback to first grouping
          const initialActiveGroupingId = answer.grouping_id || answer.mainGroupingId || firstGroupingIdIfAny;

          setLocationGroupings(fetchedGroupings);
          setLocationAssignments(fetchedAssignments);
          setFormDataByGrouping(fetchedFormDataByGrouping);
          setActiveLocationGrouping(initialActiveGroupingId);
        }

        // For pre-loaded data, no additional loading needed
      } else {
        // For new answers, data structure is already set up above
        setLocationGroupings([]);
        setLocationAssignments({});
        setFormDataByGrouping({});
        setActiveLocationGrouping(null);
      }

      initializedRef.current = true;
    } else {
      // Reset when modal closes
      initializedRef.current = false;
    }
  }, [isOpen, answer?.id, isNew, isPreLoaded, preSelectedCategoryId]); // Added preSelectedCategoryId to dependencies

  // Sync form fields when switching grouping (similar to SimpleAnswerModal)
  useEffect(() => {
    if (locationGroupings.length > 1 && activeLocationGrouping) {
      // Seed blank grouping stash if not present
      if (!formDataByGrouping[activeLocationGrouping]) {
        setFormDataByGrouping(prev => ({
          ...prev,
          [activeLocationGrouping]: { content: '' }
        }));
        setFormData(prev => ({ ...prev, content: '' }));
        return;
      }
      const grpFields = formDataByGrouping[activeLocationGrouping];
      setFormData(prev => ({
        ...prev,
        content: grpFields.content ?? ''
      }));
    }
  }, [activeLocationGrouping]);

  // Auto-select newly created category
  useEffect(() => {
    if (newlyCreatedCategory && isOpen) {
      // Add the category to the local list if it's not already there
      setCategories(prev => {
        const exists = prev.some(cat => cat.id === newlyCreatedCategory.id);
        if (!exists) {
          return [...prev, newlyCreatedCategory];
        }
        return prev;
      });

      // Select the new category
      setFormData(prev => ({
        ...prev,
        category_id: newlyCreatedCategory.id
      }));

      // Clear validation error for category
      if (validationErrors.category_id) {
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.category_id;
          return newErrors;
        });
      }

      setHasUnsavedChanges(true);

      // Clear the newly created category after using it
      if (onClearNewlyCreatedCategory) {
        onClearNewlyCreatedCategory();
      }
    }
  }, [newlyCreatedCategory, isOpen, onClearNewlyCreatedCategory, validationErrors.category_id]);

  // Add voice form filling event listener
  useEffect(() => {
    if (!isOpen) return;

    const handleVoiceFormFill = (event) => {
      const { fieldName, value, modalType } = event.detail;

      // Only handle events for this modal type or universal events
      if (modalType && modalType !== 'complex_answer') return;

      console.log('🎤 Voice filling Complex Answer field:', fieldName, '=', value);

      // Map voice field names to form fields
      const fieldMapping = {
        'name': 'name',
        'title': 'name',
        'description': 'description',
        'content': 'content',
        'answer': 'content',
        'text': 'content',
        'body': 'content',
        'complex content': 'content',
        'data': 'content'
      };

      const normalizedFieldName = fieldName.toLowerCase().trim();
      const actualFieldName = fieldMapping[normalizedFieldName] || normalizedFieldName;

      // Update the appropriate field
      if (['name', 'description', 'content'].includes(actualFieldName)) {
        setFormData(prev => ({
          ...prev,
          [actualFieldName]: value
        }));
        setHasUnsavedChanges(true);

        // Clear validation error
        if (validationErrors[actualFieldName]) {
          setValidationErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[actualFieldName];
            return newErrors;
          });
        }

        console.log('✅ Complex Answer field updated via voice:', actualFieldName);
      } else {
        console.warn('Unknown field name for Complex Answer:', fieldName);
      }
    };

    window.addEventListener('fillFormField', handleVoiceFormFill);

    return () => {
      window.removeEventListener('fillFormField', handleVoiceFormFill);
    };
  }, [isOpen, validationErrors]);

  // Handle field changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    setHasUnsavedChanges(true);
  };

  // Handle content changes with location variants (similar to SimpleAnswerModal)
  const handleContentChangeWithVariants = (e, groupingId = activeLocationGrouping) => {
    const { name, value } = e.target;

    // 1) Stash variant value
    setFormDataByGrouping(prev => ({
      ...prev,
      [groupingId]: {
        ...(prev[groupingId] || {}),
        [name]: value
      }
    }));

    // 2) Keep main form in sync for immediate UI feedback
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // 3) Clear validation error if any
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const ne = { ...prev };
        delete ne[name];
        return ne;
      });
    }
    setHasUnsavedChanges(true);
    setHasLocationChanges(true);
  };

  // Handle modal close with unsaved changes check
  const handleCloseAttempt = () => {
    if (isInline) {
      onClose(); // In inline mode, just call close handler
      return;
    }
    if (hasUnsavedChanges) {
      setIsUnsavedChangesModalOpen(true);
    } else {
      handleCloseConfirmed();
    }
  };

  // Confirmed close handling
  const handleCloseConfirmed = () => {
    initializedRef.current = false;
    setIsUnsavedChangesModalOpen(false);
    onClose();
  };

  // Form validation
  const validateForm = () => {
    const errors = {};

    // Required fields
    if (!formData.name.trim()) {
      errors.name = "Name is required";
    }

    // Validate content based on answer type
    if (!hideContentTab) {
      if (formData.answer_type === 'knowledge') {
        if (!formData.content.trim()) {
          errors.content = "Content is required for knowledge-based answers";
        }
      } else if (formData.answer_type === 'api') {
        if (!formData.api_endpoint.trim()) {
          errors.api_endpoint = "API endpoint is required for API-based answers";
        }

        // Validate API endpoint URL format
        if (formData.api_endpoint && formData.api_endpoint.trim()) {
          try {
            new URL(formData.api_endpoint);
          } catch {
            errors.api_endpoint = "Please enter a valid URL";
          }
        }

        if (showResponseTemplate && !formData.api_response_template.trim()) {
          errors.api_response_template = "Response template is required when enabled";
        }

        // Validate JSON fields if they're provided
        // API headers validation is handled by the ApiHeadersInput component

        if (formData.api_body_template && typeof formData.api_body_template === 'string') {
          try {
            // Replace question placeholders with dummy values for validation
            const tempJson = formData.api_body_template.replace(/"{{question_\d+}}"/g, '"PLACEHOLDER"');
            JSON.parse(tempJson);
          } catch {
            errors.api_body_template = "API body template must be valid JSON format";
          }
        }
      }
    }

    if (formData.triggers.length === 0) {
      errors.triggers = "At least one keyword trigger is required";
    }

    if (!formData.category_id) {
      errors.category_id = "Category is required";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error("Please fix the errors before saving");
      return;
    }

    setIsSubmitting(true);

    try {
      // Determine if we are updating or creating
      // Check if this is a real database ID or a temporary ID
      const isUpdate = answer && answer.id && !answer.id.toString().startsWith('add-') && !answer.id.toString().startsWith('edit-') && !answer.id.toString().startsWith('new-');
      const settingsId = isUpdate ? answer.id : null;

      // --- Step 1: Upsert brain_settings ---
      let upsertedSettingsId;
      if (isUpdate) {
        const { error } = await supabase
          .from('brain_settings')
          .update({
            name: formData.name,
            description: formData.description || null,
            category_id: formData.category_id,
            triggers: formData.triggers,
            type: 'complex_answer'
          })
          .eq('id', settingsId);
        if (error) throw error;
        upsertedSettingsId = settingsId;
      } else {
        const { data, error } = await supabase
          .from('brain_settings')
          .insert({
            name: formData.name,
            description: formData.description || null,
            category_id: formData.category_id,
            triggers: formData.triggers,
            type: 'complex_answer'
          })
          .select('id')
          .single();
        if (error) throw error;
        upsertedSettingsId = data.id;
      }

      // --- Step 2: Handle groupings and complex answers ---
      let complexAnswerId;
      let targetGroupingId;

        if (isUpdate) {
        // Find or create the target grouping
          if (activeLocationGrouping) {
          targetGroupingId = activeLocationGrouping;
          } else {
          // Find the first existing grouping or create a default one
            const { data: existingGroupings } = await supabase
              .from('brain_groupings')
              .select('id')
              .eq('settings_id', upsertedSettingsId)
              .order('created_at')
              .limit(1);

            if (existingGroupings && existingGroupings.length > 0) {
            targetGroupingId = existingGroupings[0].id;
            } else {
            // Create default grouping
              const { data: newGrouping, error: groupingErr } = await supabase
                .from('brain_groupings')
                .insert({ settings_id: upsertedSettingsId, name: 'Default' })
                .select('id')
                .single();
              if (groupingErr) throw groupingErr;
            targetGroupingId = newGrouping.id;
          }
        }

        // Upsert the complex answer row (only if content tab is not hidden)
        if (!hideContentTab) {
          const { data: complexAnswerData, error: complexAnswerErr } = await supabase
            .from('brain_complex_answers')
            .upsert({
              settings_id: upsertedSettingsId,
              grouping_id: targetGroupingId,
              content: formData.answer_type === 'knowledge' ? formData.content : null,
              answer_type: formData.answer_type
            }, {
              onConflict: 'settings_id,grouping_id'
            })
            .select('id')
            .single();

          if (complexAnswerErr) throw complexAnswerErr;
          complexAnswerId = complexAnswerData.id;

          // Handle API configuration separately for API-based answers
          if (formData.answer_type === 'api') {
            const { error: apiErr } = await supabase
              .from('brain_complex_answers_api')
              .upsert({
                complex_answer_id: complexAnswerId,
                api_endpoint: formData.api_endpoint,
                api_method: formData.api_method,
                api_headers: safeJsonParse(formData.api_headers, {}),
                api_query_params: safeJsonParse(formData.api_query_params, {}),
                api_body_template: safeJsonParse(formData.api_body_template, {}),
                api_response_template: showResponseTemplate ? formData.api_response_template : '',
                auth_type: formData.auth_type || 'none',
                auth_config: formData.auth_config || {}
              }, {
                onConflict: 'complex_answer_id'
              });

            if (apiErr) throw apiErr;
          } else {
            // For knowledge-based answers, remove any existing API configuration
            const { error: deleteApiErr } = await supabase
              .from('brain_complex_answers_api')
              .delete()
              .eq('complex_answer_id', complexAnswerId);

            if (deleteApiErr) throw deleteApiErr;
          }
        } else {
          // For updates when content tab is hidden, find existing complex answer
          const { data: existingAnswer } = await supabase
            .from('brain_complex_answers')
            .select('id')
            .eq('settings_id', upsertedSettingsId)
            .eq('grouping_id', targetGroupingId)
            .single();

          if (existingAnswer) {
            complexAnswerId = existingAnswer.id;
          } else {
            // Create minimal complex answer record if none exists
            const { data: complexAnswerData, error: complexAnswerErr } = await supabase
                .from('brain_complex_answers')
                .insert({
                  settings_id: upsertedSettingsId,
                grouping_id: targetGroupingId,
                content: !hideContentTab && formData.answer_type === 'knowledge' ? formData.content : null,
                answer_type: formData.answer_type
              })
              .select('id')
              .single();
            if (complexAnswerErr) throw complexAnswerErr;
            complexAnswerId = complexAnswerData.id;

            // Handle API configuration separately for API-based answers
            if (formData.answer_type === 'api') {
              const { error: apiErr } = await supabase
                .from('brain_complex_answers_api')
                .insert({
                  complex_answer_id: complexAnswerId,
                  api_endpoint: formData.api_endpoint,
                  api_method: formData.api_method,
                  api_headers: safeJsonParse(formData.api_headers, {}),
                  api_query_params: safeJsonParse(formData.api_query_params, {}),
                  api_body_template: safeJsonParse(formData.api_body_template, {}),
                  api_response_template: formData.api_response_template,
                  auth_type: formData.auth_type || 'none',
                  auth_config: formData.auth_config || {}
                });

              if (apiErr) throw apiErr;
            }
          }
        }

        } else {
          // CREATE: Create new default grouping and complex answer
          const { data: newGrouping, error: groupingErr } = await supabase
            .from('brain_groupings')
            .insert({ settings_id: upsertedSettingsId, name: 'Default' })
            .select('id')
            .single();
          if (groupingErr) throw groupingErr;
        targetGroupingId = newGrouping.id;

        const { data: complexAnswerData, error: complexAnswerErr } = await supabase
            .from('brain_complex_answers')
            .insert({
              settings_id: upsertedSettingsId,
            grouping_id: targetGroupingId,
            content: !hideContentTab && formData.answer_type === 'knowledge' ? formData.content : null,
            answer_type: formData.answer_type
          })
          .select('id')
          .single();
        if (complexAnswerErr) throw complexAnswerErr;
        complexAnswerId = complexAnswerData.id;

        // Handle API configuration separately for API-based answers
        if (formData.answer_type === 'api') {
          const { error: apiErr } = await supabase
            .from('brain_complex_answers_api')
            .insert({
              complex_answer_id: complexAnswerId,
              api_endpoint: formData.api_endpoint,
              api_method: formData.api_method,
              api_headers: safeJsonParse(formData.api_headers, {}),
              api_query_params: safeJsonParse(formData.api_query_params, {}),
              api_body_template: safeJsonParse(formData.api_body_template, {}),
              api_response_template: formData.api_response_template,
              auth_type: formData.auth_type || 'none',
              auth_config: formData.auth_config || {}
            });

          if (apiErr) throw apiErr;
        }
      }

      // --- Step 3: Handle questions ---
      if (formData.questions && formData.questions.length > 0) {
        // Delete existing questions for this complex answer (efficient batch operation)
        const { error: deleteErr } = await supabase
          .from('brain_complex_answers_questions')
          .delete()
          .eq('complex_answer_id', complexAnswerId);
        if (deleteErr) throw deleteErr;

        // Insert new questions and handle enum options
        for (let index = 0; index < formData.questions.length; index++) {
          const question = formData.questions[index];

          // Insert the question
          const { data: insertedQuestion, error: questionErr } = await supabase
            .from('brain_complex_answers_questions')
            .insert({
          complex_answer_id: complexAnswerId,
              order: index + 1,
          question: question.question,
          type: question.type
            })
            .select('id')
            .single();

          if (questionErr) throw questionErr;

          // If it's an enum question, insert the options
          if (question.type === 'enum' && question.options && question.options.length > 0) {
            const enumOptionsToInsert = question.options.map(option => ({
              complex_answer_question_id: insertedQuestion.id,
              enum_value: option
            }));

            const { error: enumErr } = await supabase
              .from('brain_complex_answers_questions_enum')
              .insert(enumOptionsToInsert);

            if (enumErr) throw enumErr;
          }
        }
      } else if (isUpdate) {
        // If no questions in form data for update, remove existing questions
        const { error: deleteErr } = await supabase
          .from('brain_complex_answers_questions')
          .delete()
          .eq('complex_answer_id', complexAnswerId);
        if (deleteErr) throw deleteErr;
      }

      // --- Step 4: Handle Requirements (for both create and update) ---
      if (upsertedSettingsId) {
        const { error: deleteReqErr } = await supabase
          .from('brain_setting_requirements')
          .delete()
          .eq('settings_id', upsertedSettingsId);
        if (deleteReqErr) throw deleteReqErr;

        if (requirements.length > 0) {
          const newRequirements = requirements.map(req => ({
            settings_id: upsertedSettingsId,
            topic_id: req.topic_id,
            required_answer: req.required_answer,
          }));
          const { error: insertReqErr } = await supabase
            .from('brain_setting_requirements')
            .insert(newRequirements);
          if (insertReqErr) throw insertReqErr;
        }
      }

      // --- Finalization ---
      toast.success(isUpdate ? "Complex answer updated successfully" : "Complex answer created successfully");

      // Run automatic conflict detection after successful save
      runAutomaticConflictDetection(supabase);

      if (isUpdate) {
        // **OPTIMISTIC UI UPDATE**: Update parent categories state immediately
        if (onUpdateSetting) {
          onUpdateSetting(upsertedSettingsId, {
            name: formData.name,
            category_id: formData.category_id
          });
        }

        // Pass the settings ID for coverage analysis
        const updateResult = { 
          success: true, 
          settingsId: upsertedSettingsId,
          isUpdate: true 
        };
        onClose(updateResult);
      } else {
        // CREATE: Create formatted setting data for local UI update
        const newSettingData = {
          id: upsertedSettingsId,
          name: formData.name,
          description: formData.description || '',
          category_id: formData.category_id,
          triggers: formData.triggers || [],
          type: 'complex_answer',
          original_type: 'complex_answer',
          fullData: {
            settings_id: upsertedSettingsId,
            name: formData.name,
            description: formData.description || '',
            content: formData.answer_type === 'knowledge' ? formData.content : null,
            answer_type: formData.answer_type,
            questions: formData.questions || [],
            category_id: formData.category_id,
            grouping_id: targetGroupingId
          },
          answer_content: formData.answer_type === 'knowledge' ? formData.content : null,
          answer_title: formData.name,
          locationVariants: [] // Start with empty variants, will be populated when fetched
        };

        // Pass the complete new data for local UI update
        const createResult = { 
          success: true, 
          settingsId: upsertedSettingsId,
          isCreate: true,
          data: newSettingData
        };
        onClose(createResult);
      }

      initializedRef.current = false;
      setHasUnsavedChanges(false);

    } catch (error) {
      console.error('Error saving complex answer:', error);
      toast.error("Failed to save complex answer: " + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle deletion
  const handleDelete = async () => {
    try {
      // Add a delete flag to the formData
      const deleteFormData = {
        ...formData,
        _delete: true
      };

      // Send to parent component for processing
      const result = await onSave(deleteFormData);

      if (result) {
        // Reset state and close modals
        setIsDeleteModalOpen(false);
        initializedRef.current = false;
        onClose();
      }
    } catch (error) {
      console.error('Error deleting complex answer:', error);
      toast.error("Failed to delete complex answer");
    }
  };

  // Function to reorder questions after drag and drop
  const handleReorderQuestions = (reorderedQuestions) => {
    setFormData(prev => ({
      ...prev,
      questions: reorderedQuestions.map((q, index) => ({
        ...q,
        order: index + 1
      }))
    }));
    setHasUnsavedChanges(true);
  };

  // Function to move a question up or down in the order
  const moveQuestion = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= formData.questions.length) return;

    const newQuestions = [...formData.questions];
    const temp = newQuestions[index];
    newQuestions[index] = newQuestions[newIndex];
    newQuestions[newIndex] = temp;

    // Update order numbers
    const reorderedQuestions = newQuestions.map((q, idx) => ({
      ...q,
      order: idx + 1
    }));

    setFormData(prev => ({
      ...prev,
      questions: reorderedQuestions
    }));
    setHasUnsavedChanges(true);
  };

  const handleCategoryChange = (value) => {
    if (value === 'new') {
      if (onCreateCategory) {
        onCreateCategory();
      }
      return;
    }
    setFormData(prev => ({ ...prev, category_id: value }));
    if (validationErrors.category_id) {
      setValidationErrors(prev => { const ne = { ...prev }; delete ne.category_id; return ne; });
    }
    setHasUnsavedChanges(true);
  };



  // API Testing functionality - elegant and simple
  const handleTestAPI = async () => {
    // Steve Jobs would want this to "just work" - validate requirements first
    if (!formData.api_endpoint) {
      toast.error("Please enter an API endpoint first");
      return;
    }

    if (!formData.questions || formData.questions.length === 0) {
      toast.error("Please add at least one question to test the API");
      return;
    }

    setIsTestingAPI(true);
    setTestResult(null);

    try {
      // Get the actual brain_complex_answers.id from the database
      let complexAnswerId = null;
      if (formData.id) {
        // Query brain_complex_answers table using the settings_id
        const { data: complexAnswerRows, error: fetchError } = await supabase
          .from('brain_complex_answers')
          .select('id')
          .eq('settings_id', formData.id)
          .limit(1);

        if (fetchError) {
          console.error('Error fetching complex answer ID:', fetchError);
          toast.error("Failed to get complex answer ID for testing");
          return;
        }

        if (complexAnswerRows && complexAnswerRows.length > 0) {
          complexAnswerId = complexAnswerRows[0].id;
        } else {
          toast.error("No complex answer record found for testing");
          return;
        }
      } else {
        toast.error("No settings ID available for testing");
        return;
      }

      // Initialize test inputs with sample data if empty
      const currentTestInputs = { ...testInputs };
      let hasEmptyInputs = false;

      formData.questions.forEach((question, index) => {
        const questionKey = `question_${index + 1}_response`;
        if (!currentTestInputs[questionKey]) {
          hasEmptyInputs = true;
          // Provide intelligent defaults based on question type
          switch (question.type) {
            case 'boolean':
              currentTestInputs[questionKey] = 'yes';
              break;
            case 'numeric':
              currentTestInputs[questionKey] = '1';
              break;
            default:
              currentTestInputs[questionKey] = `Sample answer for ${question.question}`;
          }
        }
      });

      if (hasEmptyInputs) {
        setTestInputs(currentTestInputs);
      }


      // Process the API endpoint URL with variable substitution
      let processedApiEndpoint = formData.api_endpoint || "";

      // Safety check: ensure currentTestInputs exists and is an object
      if (currentTestInputs && typeof currentTestInputs === "object") {
        Object.entries(currentTestInputs).forEach(([key, value]) => {
          // Safety check: ensure key and value are valid
          if (key && value !== undefined && value !== null) {
            const placeholder = `{{${key}}}`;
            try {
              // Escape special regex characters and replace globally
              const escapedPlaceholder = placeholder.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
              processedApiEndpoint = processedApiEndpoint.replace(
                new RegExp(escapedPlaceholder, "g"), 
                encodeURIComponent(String(value))
              );
            } catch (error) {
              console.warn(`Error replacing placeholder ${placeholder}:`, error);
            }
          }
        });
      }

      // Create the request body with the correct structure for complex answers
      const requestBody = {
        type: "complex_answer_api_call",
        complexAnswerId: complexAnswerId,
        responses: currentTestInputs
      };

      // Get current user for the request
      const { getUserAccountId } = await import('../../utils/accountUtils');
      const { accountId } = await getUserAccountId();
      if (!accountId) {
        toast.error("User authentication required for API testing");
        return;
      }

      // Make the API call through your backend - use the correct complex answer endpoint
      const response = await fetch(`${API_BASE_URL}/api/external/complex-answer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          account_id: accountId, // Add account ID for integration resolution
          api_endpoint: processedApiEndpoint,
          api_method: formData.api_method || 'POST',
          api_headers: safeJsonParse(formData.api_headers, {}),
          api_body: requestBody,
          auth_type: formData.auth_type || 'none',
          auth_config: formData.auth_config || {},
          actionId: complexAnswerId
        })
      });

      // Handle both JSON and non-JSON responses gracefully
      let responseData;
      const contentType = response.headers.get('content-type');

      try {
        if (contentType && contentType.includes('application/json')) {
          responseData = await response.json();
        } else {
          // Handle plain text responses (like "Accepted" from Make.com)
          const textResponse = await response.text();
          responseData = { message: textResponse, raw: textResponse };
        }
      } catch (parseError) {
        // If JSON parsing fails, fall back to text
        const textResponse = await response.text();
        responseData = { message: textResponse, raw: textResponse };
      }

      if (!response.ok) {
        const errorMessage = responseData.message || responseData.error || responseData.raw || 'Unknown error';
        throw new Error(`API returned ${response.status}: ${errorMessage}`);
      }

      // Success - store the result
      setTestResult({
        success: true,
        data: responseData,
        status: response.status,
        timestamp: new Date().toISOString()
      });

      toast.success("API test successful! 🎉");

    } catch (error) {
      console.error('API test failed:', error);
      setTestResult({
        success: false,
        error: error.message,
        timestamp: new Date().toISOString()
      });
      toast.error(`API test failed: ${error.message}`);
    } finally {
      setIsTestingAPI(false);
    }
  };

  // If not open, don't render
  if (!isOpen) return null;

  // Step Content Renderers
  const renderSetupStep = () => (
      <motion.div 
                initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="space-y-8"
    >
      <div className="mb-8">
        <div className="flex items-start space-x-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center shadow-sm border border-gray-200/50 flex-shrink-0">
            <PencilSimple className="w-5 h-5 text-gray-700" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-medium text-gray-900 text-sm">Setup Your Workflow</h2>
            <p className="text-gray-500 text-xs mt-0.5">Give your conversational flow a name and description</p>
          </div>
        </div>
            </div>

      <div className={cn(
        "space-y-6 mx-auto transition-all duration-300",
        isSidePanelOpen ? "max-w-lg" : "max-w-2xl"
      )}>
                    {/* Description Field */}
                    <div className="space-y-2">
          <Label htmlFor="description" className="text-sm font-medium text-gray-700">
            Description <span className="text-gray-400 text-xs">(Optional)</span>
                      </Label>
                      <Textarea
                        id="description"
                        name="description"
                        value={formData.description || ''}
                        onChange={handleChange}
            placeholder="Describe what this workflow accomplishes..."
            className="min-h-[100px] text-sm border-gray-200 bg-white focus:border-purple-500 focus:ring-purple-500 rounded-xl resize-none"
                      />
                    </div>

        {/* Navigation - Only show for new workflows */}
        {isNew && (
          <div className="flex justify-end pt-4">
          <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setCurrentStep('triggers')}
              disabled={!isStepCompleted('setup')}
                      className={cn(
                "px-6 py-2.5 rounded-xl text-sm font-medium transition-colors",
                isStepCompleted('setup')
                  ? "bg-purple-500 hover:bg-purple-600 text-white shadow-sm"
                  : "bg-gray-200 text-gray-500 cursor-not-allowed"
              )}
            >
              Continue to Triggers
          </motion.button>
          </div>
        )}
                  </div>
          </motion.div>
  );

  const renderTriggersStep = () => (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="space-y-8"
    >
            <div className="flex items-start space-x-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center shadow-sm border border-gray-200/50 flex-shrink-0">
            <Lightning className="w-5 h-5 text-gray-700" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-medium text-gray-900 text-sm">Add Trigger Keywords</h2>
            <p className="text-gray-500 text-xs mt-0.5">Define words or phrases that will activate this workflow</p>
          </div>
        </div>

      <div className={cn(
        "space-y-6 mx-auto transition-all duration-300",
        isSidePanelOpen ? "max-w-lg" : "max-w-2xl"
      )}>
                    {/* Display existing triggers */}
        <div className="min-h-[120px] bg-gray-50 rounded-xl p-4 border border-gray-200">
                        {formData.triggers.length === 0 ? (
                        <div className="h-full flex flex-col items-center justify-center text-center py-8">
                          <Chat className="h-8 w-8 text-gray-300 mb-2" />
                          <p className="text-sm text-gray-400">No keywords added yet</p>
              <p className="text-xs text-gray-400 mt-1">Add some keywords to trigger this workflow</p>
                          </div>
                        ) : (
                        <div className="flex flex-wrap gap-2">
                            <AnimatePresence>
                              {formData.triggers.map((trigger, index) => (
                                <motion.div
                                  key={`${trigger}-${index}`}
                                  initial={{ opacity: 0, scale: 0.8 }}
                                  animate={{ opacity: 1, scale: 1 }}
                                  exit={{ opacity: 0, scale: 0.8, transition: { duration: 0.15 } }}
                                  transition={{ type: "spring", stiffness: 400, damping: 25 }}
                    className="bg-white border border-gray-200 text-gray-800 text-xs px-2.5 py-1.5 rounded-lg flex items-center shadow-sm"
                                >
                                  <span>{trigger}</span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const newTriggers = formData.triggers.filter(t => t !== trigger);
                                      setFormData(prev => ({ ...prev, triggers: newTriggers }));
                                      setHasUnsavedChanges(true);
                                    }}
                                  className="ml-1.5 text-gray-400 hover:text-gray-600 focus:outline-none p-0.5"
                                  >
                                  <X className="h-3 w-3" />
                                  </button>
                                </motion.div>
                              ))}
                            </AnimatePresence>
                          </div>
                        )}
                      </div>

                      {validationErrors.triggers && (
                        <motion.p 
                        className="text-xs text-red-500 flex items-center"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                        >
                          {validationErrors.triggers}
                        </motion.p>
                      )}

                    {/* Add trigger form */}
                        <form onSubmit={(e) => {
                          e.preventDefault();
                          if (!currentTrigger.trim()) return;
                          const newTrigger = currentTrigger.trim().toLowerCase();
                          if (formData.triggers.includes(newTrigger)) {
                            setCurrentTrigger('');
                            return;
                          }
          setFormData(prev => ({ ...prev, triggers: [...prev.triggers, newTrigger] }));
                          setCurrentTrigger('');
                          if (validationErrors.triggers) {
                            setValidationErrors(prev => {
                              const newErrors = { ...prev };
                              delete newErrors.triggers;
                              return newErrors;
                            });
                          }
                          setHasUnsavedChanges(true);
                        }} className="flex space-x-2">
                          <Input
                            value={currentTrigger}
                            onChange={(e) => setCurrentTrigger(e.target.value)}
                            placeholder="Type a keyword and press Enter"
            className="flex-1 h-11 text-sm border-gray-200 bg-white focus:border-purple-500 focus:ring-purple-500 rounded-xl"
                          />
                          <Button 
                            type="submit" 
                            disabled={!currentTrigger.trim()}
            className="whitespace-nowrap text-sm px-4 h-11 bg-purple-500 hover:bg-purple-600 rounded-xl"
                          >
                        <Plus className="h-4 w-4 mr-1" />
                            Add
                          </Button>
                        </form>

                  {/* Qualifiers Section */}
        {accountId && (
          <div className="pt-4 border-t border-gray-100">
            <BrainSettingConditions
              settingId={formData.id}
              accountId={accountId}
              initialRequirements={requirements}
              onRequirementsChange={(newReqs) => {
                setRequirements(newReqs);
                setHasUnsavedChanges(true);
              }}
            />
          </div>
        )}

        {/* Navigation - Only show for new workflows */}
        {isNew && (
          <div className="flex justify-between pt-4">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={onClose}
              className="px-6 py-2.5 rounded-xl text-sm font-medium transition-colors bg-gray-100 hover:bg-gray-200 text-gray-700"
            >
              Cancel
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setCurrentStep('questions')}
              disabled={!isStepCompleted('triggers')}
              className={cn(
                "px-6 py-2.5 rounded-xl text-sm font-medium transition-colors",
                isStepCompleted('triggers')
                  ? "bg-purple-500 hover:bg-purple-600 text-white shadow-sm"
                  : "bg-gray-200 text-gray-500 cursor-not-allowed"
              )}
            >
              Continue to Questions
            </motion.button>
          </div>
        )}
                      </div>
                </motion.div>
  );

  const renderQuestionsStep = () => (
                <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="space-y-8"
    >
            <div className="flex items-start space-x-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center shadow-sm border border-gray-200/50 flex-shrink-0">
            <Question className="w-5 h-5 text-gray-700" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-medium text-gray-900 text-sm">Configure Questions</h2>
            <p className="text-gray-500 text-xs mt-0.5">Ask customers what you need to know to help them (optional)</p>
          </div>
        </div>

      <div className={cn(
        "space-y-6 mx-auto transition-all duration-300",
        isSidePanelOpen ? "max-w-lg" : "max-w-2xl"
      )}>
                      {/* Display existing questions */}
        <div className="min-h-[200px] bg-gray-50 rounded-xl p-4 border border-gray-200">
                        {formData.questions.length === 0 ? (
                        <div className="h-full flex flex-col items-center justify-center text-center py-8">
                          <Question className="h-8 w-8 text-gray-300 mb-2" />
                          <p className="text-sm text-gray-400">No questions yet</p>
              <p className="text-xs text-gray-400 mt-1">Ask your customers what you need to know to help them</p>
                          </div>
                        ) : (
                        <div className="space-y-3">
                            <AnimatePresence>
                              {formData.questions.map((question, index) => (
                                <motion.div
                                  key={question.id || `question-${index}`}
                                  initial={{ opacity: 0, y: -10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  exit={{ opacity: 0, y: -10, transition: { duration: 0.15 } }}
                                  transition={{ duration: 0.2 }}
                    className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden"
                                >
                                <div className="flex items-center border-b border-gray-100 px-4 py-3">
                                  <div className="flex-shrink-0 mr-3 flex items-center">
                                    <span className="text-sm font-medium text-gray-500 bg-gray-100 rounded-full w-6 h-6 flex items-center justify-center">
                                        {index + 1}
                                      </span>
                                    </div>
                                    <div className="flex-1 flex items-center">
                                    <div className={`text-xs px-2 py-1 rounded ${
                                        question.type === 'boolean' ? 'bg-green-100 text-green-800' :
                                        question.type === 'numeric' ? 'bg-blue-100 text-blue-800' :
                                        question.type === 'date' ? 'bg-orange-100 text-orange-800' :
                                        question.type === 'enum' ? 'bg-indigo-100 text-indigo-800' :
                                        'bg-purple-100 text-purple-800'
                                      }`}>
                                        {question.type === 'boolean' ? 'Yes/No' :
                                         question.type === 'numeric' ? 'Number' :
                                         question.type === 'date' ? 'Date' :
                                         question.type === 'enum' ? 'Choice' : 'Text'}
                                      </div>
                                    </div>
                                    <div className="flex items-center space-x-1">
                                      {index > 0 && (
                          <button type="button" onClick={() => moveQuestion(index, 'up')} className="p-1 text-gray-400 hover:text-gray-600 focus:outline-none" aria-label="Move up">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><path d="m18 15-6-6-6 6"/></svg>
                                        </button>
                                      )}
                                      {index < formData.questions.length - 1 && (
                          <button type="button" onClick={() => moveQuestion(index, 'down')} className="p-1 text-gray-400 hover:text-gray-600 focus:outline-none" aria-label="Move down">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><path d="m6 9 6 6 6-6"/></svg>
                                        </button>
                                      )}
                        <button type="button" onClick={() => {
                                          const newQuestions = [...formData.questions];
                                          newQuestions.splice(index, 1);
                          const reorderedQuestions = newQuestions.map((q, idx) => ({ ...q, order: idx + 1 }));
                                          setFormData(prev => ({ ...prev, questions: reorderedQuestions }));
                                          setHasUnsavedChanges(true);
                        }} className="p-1 text-gray-400 hover:text-red-500 focus:outline-none" aria-label="Delete question">
                                      <X className="h-4 w-4" />
                                      </button>
                                    </div>
                                  </div>
                                <div className="px-4 py-3">
                                    <p className="text-sm text-gray-800">{question.question}</p>
                                  {question.type === 'enum' && question.options && question.options.length > 0 && (
                                    <div className="mt-2 flex flex-wrap gap-1">
                                      {question.options.map((option, optIndex) => (
                                        <span key={optIndex} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                                          {option}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                  </div>
                                </motion.div>
                              ))}
                            </AnimatePresence>
                          </div>
                        )}
                      </div>

                      {/* Add question form */}
                        <form onSubmit={(e) => {
                          e.preventDefault();
                          if (!currentQuestion.trim()) return;
                          const newQuestion = {
                        id: null,
                            question: currentQuestion.trim(),
                            type: currentQuestionType,
                            order: formData.questions.length + 1,
                            options: currentQuestionType === 'enum' ? [...currentQuestionOptions] : []
                          };
          setFormData(prev => ({ ...prev, questions: [...prev.questions, newQuestion] }));
                          setCurrentQuestion('');
                          setCurrentQuestionOptions([]);
                          setNewOption('');
                          setHasUnsavedChanges(true);
                    }} className="space-y-3">
                          <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-2">
                            <Input
                              value={currentQuestion}
                              onChange={(e) => setCurrentQuestion(e.target.value)}
                              placeholder="Add a question..."
              className="flex-1 h-11 text-sm border-gray-200 bg-white focus:border-purple-500 focus:ring-purple-500 rounded-xl"
            />
            <Select value={currentQuestionType} onValueChange={(value) => { setCurrentQuestionType(value); if (value !== 'enum') { setCurrentQuestionOptions([]); } }}>
              <SelectTrigger className="w-full sm:w-40 h-11 text-sm border-gray-200 bg-white focus:border-purple-500 focus:ring-purple-500 rounded-xl">
                                <SelectValue placeholder="Type" />
                              </SelectTrigger>
                              <SelectContent className="z-[10002]">
                                <SelectItem value="string">Text</SelectItem>
                                <SelectItem value="boolean">Yes/No</SelectItem>
                                <SelectItem value="numeric">Number</SelectItem>
                                <SelectItem value="date">Date</SelectItem>
                                <SelectItem value="enum">Multiple Choice</SelectItem>
                              </SelectContent>
                            </Select>
            <Button type="submit" disabled={!currentQuestion.trim() || (currentQuestionType === 'enum' && currentQuestionOptions.length === 0)} className="whitespace-nowrap text-sm px-4 h-11 bg-purple-500 hover:bg-purple-600 rounded-xl">
              <Plus className="h-4 w-4 mr-1" /> Add
                            </Button>
                          </div>
                          {currentQuestionType === 'enum' && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="bg-gray-50 rounded-xl p-4 border border-gray-200 space-y-3">
                            <h4 className="text-sm font-medium text-gray-700">Multiple Choice Options</h4>
                          <div className="flex gap-2">
                                <Input
                                  value={newOption}
                                  onChange={(e) => setNewOption(e.target.value)}
                                  placeholder="Add an option..."
                  className="flex-1 h-9 text-sm border-gray-200 bg-white focus:border-purple-500 focus:ring-purple-500 rounded-xl"
                  onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (newOption.trim() && !currentQuestionOptions.includes(newOption.trim())) { setCurrentQuestionOptions([...currentQuestionOptions, newOption.trim()]); setNewOption(''); } } }}
                />
                <Button type="button" onClick={() => { if (newOption.trim() && !currentQuestionOptions.includes(newOption.trim())) { setCurrentQuestionOptions([...currentQuestionOptions, newOption.trim()]); setNewOption(''); } }} variant="outline" size="sm" disabled={!newOption.trim() || currentQuestionOptions.includes(newOption.trim())} className="bg-white border-gray-200 rounded-xl">
                                  Add
                                </Button>
                              </div>
                              {currentQuestionOptions.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-xl p-3 max-h-[120px] overflow-y-auto">
                                  {currentQuestionOptions.map((option, idx) => (
                                    <div
                                      key={idx}
                                  className="flex justify-between items-center py-1 px-2 hover:bg-gray-50 rounded text-sm"
                                    >
                                      <span className="text-gray-800">{option}</span>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setCurrentQuestionOptions(currentQuestionOptions.filter((_, i) => i !== idx));
                                        }}
                                        className="text-gray-400 hover:text-red-500 ml-2"
                                      >
                                        <X className="h-3 w-3" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </motion.div>
                          )}
                        </form>

        {/* Navigation - Only show for new workflows */}
        {isNew && (
          <div className="flex justify-between pt-4">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setCurrentStep('triggers')}
              className="px-6 py-2.5 rounded-xl text-sm font-medium transition-colors bg-gray-100 hover:bg-gray-200 text-gray-700"
            >
              Back to Triggers
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setCurrentStep('response')}
              disabled={!isStepCompleted('questions')}
              className={cn(
                "px-6 py-2.5 rounded-xl text-sm font-medium transition-colors",
                isStepCompleted('questions')
                  ? "bg-purple-500 hover:bg-purple-600 text-white shadow-sm"
                  : "bg-gray-200 text-gray-500 cursor-not-allowed"
              )}
            >
              Continue to Response
            </motion.button>
          </div>
        )}
                      </div>
                </motion.div>
  );

  const renderResponseStep = () => (
                    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="space-y-8"
    >
            <div className="mb-6">
        <div className="flex items-start space-x-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center shadow-sm border border-gray-200/50 flex-shrink-0">
            <Database className="w-5 h-5 text-gray-700" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-medium text-gray-900 text-sm">Define the Response</h2>
            <p className="text-gray-500 text-xs mt-0.5">Choose how the workflow will respond to the customer</p>
          </div>
        </div>
                    </div>

      <div className={cn(
        "space-y-6 mx-auto transition-all duration-300",
        isSidePanelOpen ? "max-w-lg" : "max-w-2xl"
      )}>
                    {/* Answer Type Selector */}
                      <div className="space-y-2">
          <Label htmlFor="answer_type" className="text-sm font-medium text-gray-700">
            Answer Type
                      </Label>
                      <Select 
                        value={formData.answer_type} 
                        onValueChange={(value) => {
                          setFormData(prev => ({ ...prev, answer_type: value }));
                          setHasUnsavedChanges(true);
                          setValidationErrors({});
                        }}
                      >
            <SelectTrigger className="h-11 text-sm border-gray-200 bg-white focus:border-purple-500 focus:ring-purple-500 rounded-xl">
                          <SelectValue placeholder="Select answer type" />
                        </SelectTrigger>
            <SelectContent className="z-[10060]">
              <SelectItem value="knowledge">Knowledge-based (Text Content)</SelectItem>
              <SelectItem value="api">API-based (External Data)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Knowledge-based Content */}
                    {formData.answer_type === 'knowledge' && (
          <div className="space-y-2">
            <Label htmlFor="content" className="text-sm font-medium text-gray-700">
              Content
            </Label>
                      <Textarea
                        id="content"
                        name="content"
                        value={formData.content}
                        onChange={handleContentChangeWithVariants}
              placeholder="Start writing your content here. Use {{question_1_response}}, etc. to insert answers."
              className="min-h-[200px] text-sm border-gray-200 bg-white focus:border-purple-500 focus:ring-purple-500 rounded-xl resize-none"
            />
                      </div>
                    )}

                    {/* API-based Content */}
                    {formData.answer_type === 'api' && (
                        <div className="space-y-6">
                          {/* API Endpoint */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">API Endpoint <span className="text-red-500">*</span></Label>
                            <div className="w-full flex space-x-2">
                <Select value={formData.api_method} onValueChange={(value) => { setFormData(prev => ({ ...prev, api_method: value })); setHasUnsavedChanges(true); }}>
                  <SelectTrigger className="w-24 h-11 text-sm font-mono border-gray-200 bg-white focus:border-purple-500 focus:ring-purple-500 rounded-xl flex-shrink-0">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="z-[10002]">
                                  <SelectItem value="GET"><span className="text-green-600 font-medium">GET</span></SelectItem>
                                  <SelectItem value="POST"><span className="text-blue-600 font-medium">POST</span></SelectItem>
                                  <SelectItem value="PUT"><span className="text-orange-600 font-medium">PUT</span></SelectItem>
                                  <SelectItem value="PATCH"><span className="text-yellow-600 font-medium">PATCH</span></SelectItem>
                                  <SelectItem value="DELETE"><span className="text-red-600 font-medium">DELETE</span></SelectItem>
                                </SelectContent>
                              </Select>
                              <div className="flex-1 min-w-0">
                                <MentionInput
                                  name="api_endpoint"
                                  value={formData.api_endpoint || ''}
                    onChange={(e) => { setFormData(prev => ({ ...prev, api_endpoint: e.target.value })); setHasUnsavedChanges(true); }}
                                  questions={questionsForMention}
                                  placeholder="https://api.example.com/endpoint"
                    className={cn("w-full h-11 text-sm font-mono border-gray-200 bg-white focus:border-purple-500 focus:ring-purple-500 rounded-xl", validationErrors.api_endpoint && "border-red-300")}
                                />
                              </div>
                            </div>
              {validationErrors.api_endpoint && (<p className="text-xs text-red-500">{validationErrors.api_endpoint}</p>)}
                          </div>

                          {/* API Headers */}
                          <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">Headers</Label>
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                            <ApiHeadersInput
                              headers={formData.api_headers}
                              onChange={(headers) => {
                                setFormData(prev => ({ ...prev, api_headers: headers }));
                                setHasUnsavedChanges(true);
                              }}
                            />
              </div>
                          </div>

                          {/* API Query Parameters */}
                          <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">Query Parameters</Label>
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                            <ApiQueryParamsInput
                              queryParams={formData.api_query_params}
                              onChange={(params) => {
                                setFormData(prev => ({ ...prev, api_query_params: params }));
                                setHasUnsavedChanges(true);
                              }}
                              questions={questionsForMention}
                            />
              </div>
                          </div>

                          {/* Authentication */}
                          <div className="space-y-4">
              <Label className="text-sm font-medium text-gray-700">Authentication</Label>

                            {/* Integration Auth Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
                              <div className="flex items-center space-x-3">
                                <Plug className="h-5 w-5 text-purple-500" />
                                <div>
                                  <p className="text-sm font-medium text-gray-900">Use Integration Authentication</p>
                                  <p className="text-xs text-gray-600">Use credentials from your connected integrations</p>
                                </div>
                              </div>
                              <Switch
                                checked={useIntegrationAuth}
                                onCheckedChange={(checked) => {
                                  setUseIntegrationAuth(checked);
                                  if (checked) {
                                    setFormData(prev => ({ ...prev, auth_type: 'integration' }));
                                  } else {
                                    setFormData(prev => ({ ...prev, auth_type: 'none', auth_config: {} }));
                                    setSelectedProviderId('');
                                  }
                                  setHasUnsavedChanges(true);
                                }}
                                className="data-[state=checked]:bg-purple-500"
                              />
                            </div>

                            {/* Integration Provider Selection */}
              {useIntegrationAuth && (
                              <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">Select Integration Provider</Label>
                  {availableProviders.length > 0 ? (
                                <Select value={selectedProviderId} onValueChange={handleIntegrationSelection}>
                      <SelectTrigger className="h-11 text-sm border-gray-200 bg-white focus:border-purple-500 focus:ring-purple-500 rounded-xl">
                                    <SelectValue placeholder="Choose an integration" />
                                  </SelectTrigger>
                                  <SelectContent className="z-[10002]">
                                    {availableProviders.map(provider => (
                                      <SelectItem key={provider.id} value={provider.id}>
                                        {provider.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                  ) : (
                    <div className="text-sm text-center text-gray-500 p-4 bg-gray-100 rounded-xl">
                      No available integrations found.
                    </div>
                  )}
                              </div>
                            )}

                            {/* Manual Auth Type Selection */}
                            {!useIntegrationAuth && (
                              <div className="space-y-4">
                                <Select 
                                  value={formData.auth_type}
                                  onValueChange={(value) => {
                                    setFormData(prev => ({ 
                                      ...prev, 
                                      auth_type: value,
                                      auth_config: value === 'none' ? {} : prev.auth_config
                                    }));
                                    setHasUnsavedChanges(true);
                                  }}
                                >
                    <SelectTrigger className="h-11 text-sm border-gray-200 bg-white focus:border-purple-500 focus:ring-purple-500 rounded-xl">
                                    <SelectValue placeholder="Select authentication type" />
                                  </SelectTrigger>
                                  <SelectContent className="z-[10002]">
                                    <SelectItem value="none">No Authentication</SelectItem>
                                    <SelectItem value="bearer">Bearer Token</SelectItem>
                                    <SelectItem value="basic">Basic Auth</SelectItem>
                                    <SelectItem value="api_key">API Key</SelectItem>
                                  </SelectContent>
                                </Select>

                                {/* Auth Configuration Fields */}
                                {formData.auth_type === 'bearer' && (
                                  <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700">Bearer Token</Label>
                                    <div className="relative">
                                      <Input
                                        type={showBearerToken ? "text" : "password"}
                                        value={formData.auth_config?.token || ''}
                                        onChange={(e) => {
                                          setFormData(prev => ({ 
                                            ...prev, 
                                            auth_config: { ...prev.auth_config, token: e.target.value }
                                          }));
                                          setHasUnsavedChanges(true);
                                        }}
                                        placeholder="Enter bearer token"
                          className="h-11 text-sm border-gray-200 bg-white focus:border-purple-500 focus:ring-purple-500 rounded-xl pr-10"
                                      />
                                      <button
                                        type="button"
                                        onClick={() => setShowBearerToken(!showBearerToken)}
                                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                      >
                                        {showBearerToken ? <EyeSlash className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                      </button>
                                    </div>
                                  </div>
                                )}

                                {formData.auth_type === 'basic' && (
                                  <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-700">Username</Label>
                                      <Input
                                        value={formData.auth_config?.username || ''}
                                        onChange={(e) => {
                                          setFormData(prev => ({ 
                                            ...prev, 
                                            auth_config: { ...prev.auth_config, username: e.target.value }
                                          }));
                                          setHasUnsavedChanges(true);
                                        }}
                                        placeholder="Username"
                          className="h-11 text-sm border-gray-200 bg-white focus:border-purple-500 focus:ring-purple-500 rounded-xl"
                                      />
                                    </div>
                                    <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-700">Password</Label>
                                      <div className="relative">
                                        <Input
                                          type={showBasicPassword ? "text" : "password"}
                                          value={formData.auth_config?.password || ''}
                                          onChange={(e) => {
                                            setFormData(prev => ({ 
                                              ...prev, 
                                              auth_config: { ...prev.auth_config, password: e.target.value }
                                            }));
                                            setHasUnsavedChanges(true);
                                          }}
                                          placeholder="Password"
                            className="h-11 text-sm border-gray-200 bg-white focus:border-purple-500 focus:ring-purple-500 rounded-xl pr-10"
                                        />
                                        <button
                                          type="button"
                                          onClick={() => setShowBasicPassword(!showBasicPassword)}
                                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        >
                                          {showBasicPassword ? <EyeSlash className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {formData.auth_type === 'api_key' && (
                                  <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-700">Key Name</Label>
                                      <Input
                                        value={formData.auth_config?.key_name || ''}
                                        onChange={(e) => {
                                          setFormData(prev => ({ 
                                            ...prev, 
                                            auth_config: { ...prev.auth_config, key_name: e.target.value }
                                          }));
                                          setHasUnsavedChanges(true);
                                        }}
                                        placeholder="X-API-Key"
                          className="h-11 text-sm border-gray-200 bg-white focus:border-purple-500 focus:ring-purple-500 rounded-xl"
                                      />
                                    </div>
                                    <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-700">API Key</Label>
                                      <div className="relative">
                                        <Input
                                          type={showApiKey ? "text" : "password"}
                                          value={formData.auth_config?.key_value || ''}
                                          onChange={(e) => {
                                            setFormData(prev => ({ 
                                              ...prev, 
                                              auth_config: { ...prev.auth_config, key_value: e.target.value }
                                            }));
                                            setHasUnsavedChanges(true);
                                          }}
                                          placeholder="Enter API key"
                            className="h-11 text-sm border-gray-200 bg-white focus:border-purple-500 focus:ring-purple-500 rounded-xl pr-10"
                                        />
                                        <button
                                          type="button"
                                          onClick={() => setShowApiKey(!showApiKey)}
                                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        >
                                          {showApiKey ? <EyeSlash className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>

                          {/* API Body Template */}
                          <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">Request Body (JSON)</Label>
                            <JsonMentionTextarea
                              name="api_body_template"
                              value={typeof formData.api_body_template === 'string' ? formData.api_body_template : JSON.stringify(formData.api_body_template, null, 2)}
                              onChange={handleApiBodyTemplateChange}
                              questions={questionsForMention}
                              placeholder='{\n  "query": "{{question_1_response}}",\n  "filters": {\n    "category": "{{question_2_response}}"\n  }\n}'
                              className={cn(
                  "min-h-[150px] text-sm border-gray-200 bg-white focus:border-purple-500 focus:ring-purple-500 rounded-xl font-mono resize-none",
                  validationErrors.api_body_template && "border-red-300"
                              )}
                            />
                            {validationErrors.api_body_template && (
                              <p className="text-xs text-red-500">{validationErrors.api_body_template}</p>
                            )}
                          </div>

                          {/* Response Template Toggle */}
                          <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
                              <div className="flex items-center space-x-3">
                                <Lightning className="h-5 w-5 text-purple-500" />
                                <div>
                                  <p className="text-sm font-medium text-gray-900">Response Template</p>
                                  <p className="text-xs text-gray-600">Transform API response before showing to customer</p>
                                </div>
                              </div>
                              <Switch
                                checked={showResponseTemplate}
                                onCheckedChange={setShowResponseTemplate}
                                className="data-[state=checked]:bg-purple-500"
                              />
                            </div>

                            {showResponseTemplate && (
                              <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">Response Template <span className="text-red-500">*</span></Label>
                                <Textarea
                                  value={formData.api_response_template || ''}
                                  onChange={(e) => {
                                    setFormData(prev => ({ ...prev, api_response_template: e.target.value }));
                                    setHasUnsavedChanges(true);
                                  }}
                                  placeholder="Use {{response}} to reference the API response data"
                                  className={cn(
                      "min-h-[100px] text-sm border-gray-200 bg-white focus:border-purple-500 focus:ring-purple-500 rounded-xl resize-none",
                      validationErrors.api_response_template && "border-red-300"
                                  )}
                                />
                                {validationErrors.api_response_template && (
                                  <p className="text-xs text-red-500">{validationErrors.api_response_template}</p>
                                )}
                              </div>
                            )}
                          </div>
          </div>
        )}

        {/* Navigation - Only show for new workflows */}
        {isNew && (
          <div className="flex justify-between pt-4">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setCurrentStep('questions')}
              className="px-6 py-2.5 rounded-xl text-sm font-medium transition-colors bg-gray-100 hover:bg-gray-200 text-gray-700"
            >
              Back to Questions
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => {
                if (formData.answer_type === 'api' && formData.questions?.length > 0) {
                  setCurrentStep('test');
                } else {
                  handleSubmit();
                }
              }}
              disabled={!isStepCompleted('response')}
              className={cn(
                "px-6 py-2.5 rounded-xl text-sm font-medium transition-colors",
                isStepCompleted('response')
                  ? "bg-purple-500 hover:bg-purple-600 text-white shadow-sm"
                  : "bg-gray-200 text-gray-500 cursor-not-allowed"
              )}
            >
              {formData.answer_type === 'api' && formData.questions?.length > 0 ? 'Continue to Test' : 'Save'}
            </motion.button>
          </div>
        )}
      </div>
    </motion.div>
  );

  const renderTestStep = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="space-y-8"
    >
      <div className="mb-8">
        <div className="flex items-start space-x-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center shadow-sm border border-gray-200/50 flex-shrink-0">
            <Play className="w-5 h-5 text-gray-700" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-medium text-gray-900 text-sm">Test Your API</h2>
            <p className="text-gray-500 text-xs mt-0.5">Validate your API endpoint with sample data</p>
          </div>
        </div>
                                  </div>

      <div className={cn(
        "space-y-6 mx-auto transition-all duration-300",
        isSidePanelOpen ? "max-w-lg" : "max-w-2xl"
      )}>
                                  {/* Test Input Fields */}
                                  <div className="space-y-3">
                                    {formData.questions.map((question, index) => (
                                      <div key={question.id || index} className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">
                                          {question.question}
                                        </Label>
                                        <Input
                                          value={testInputs[`question_${index + 1}_response`] || ''}
                                          onChange={(e) => {
                                            setTestInputs(prev => ({
                                              ...prev,
                                              [`question_${index + 1}_response`]: e.target.value
                                            }));
                                          }}
                                          placeholder={`Enter test value for: ${question.question}`}
                className="h-11 text-sm border-gray-200 bg-white focus:border-purple-500 focus:ring-purple-500 rounded-xl"
                                        />
                                      </div>
                                    ))}
                                  </div>

                                  {/* Test Button */}
        <div className="flex justify-center pt-4">
                                    <Button
                                      type="button"
                                      onClick={handleTestAPI}
                                      disabled={isTestingAPI || !formData.api_endpoint}
            className="bg-purple-500 hover:bg-purple-600 text-white px-6 py-2.5 rounded-xl shadow-sm"
                                    >
                                      {isTestingAPI ? (
              <> <CircleNotch className="animate-spin h-4 w-4 mr-2" /> Testing API </>
                                      ) : (
              <> <Play className="h-4 w-4 mr-2" /> Test API </>
                                      )}
                                    </Button>
                                  </div>

                                  {/* Test Results */}
                                  {testResult && (
          <div className="mt-6 p-4 rounded-xl border border-gray-200 bg-white shadow-sm">
                                      <div className="flex items-center space-x-2 mb-3">
                                        {testResult.success ? (
                                          <CheckCircle className="h-5 w-5 text-green-500" />
                                        ) : (
                                          <XCircle className="h-5 w-5 text-red-500" />
                                        )}
                                        <h4 className="text-sm font-medium text-gray-900">
                                          {testResult.success ? 'API Test Successful' : 'API Test Failed'}
                                        </h4>
                                      </div>
            <pre className="text-xs bg-gray-50 p-3 rounded-xl border overflow-auto max-h-40 font-mono">
                                        {JSON.stringify(testResult, null, 2)}
                                      </pre>
                                    </div>
                                  )}

        {/* Navigation - Only show for new workflows */}
        {isNew && (
          <div className="flex justify-between pt-4">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setCurrentStep('response')}
              className="px-6 py-2.5 rounded-xl text-sm font-medium transition-colors bg-gray-100 hover:bg-gray-200 text-gray-700"
            >
              Back to Response
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleSubmit}
              className="px-6 py-2.5 rounded-xl text-sm font-medium transition-colors bg-purple-500 hover:bg-purple-600 text-white shadow-sm"
            >
              Save
            </motion.button>
          </div>
        )}
      </div>
    </motion.div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 'setup':
        return renderSetupStep();
      case 'triggers':
        return renderTriggersStep();
      case 'questions':
        return renderQuestionsStep();
      case 'response':
        return renderResponseStep();
      case 'test':
        return renderTestStep();
      default:
        return renderSetupStep();
    }
  };

  const renderContent = () => (
    <motion.div
      className={cn(
        "bg-white flex flex-col overflow-hidden",
        isInline
          ? "w-full h-full"
          : "rounded-2xl shadow-xl w-[90vw] h-[90vh] relative border border-gray-200/50 overflow-hidden"
      )}
      variants={!isInline ? modalVariants : {}}
      initial={!isInline ? "hidden" : ""}
      animate={!isInline ? "visible" : ""}
      exit={!isInline ? "exit" : ""}
      onClick={e => e.stopPropagation()}
    >
      {/* 🏢 BRANDED COMPANY LETTERHEAD WITH TITLE */}
      <motion.div 
        className={cn(
          "bg-white border-b border-gray-100 py-[11px] relative z-20 flex-shrink-0 transition-all duration-300",
          isSidePanelOpen ? "px-4 sm:px-6" : "px-6 sm:px-8"
        )}
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.3 }}
      >
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-purple-500 to-purple-600"></div>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-sm">
                <Database size={16} className="text-white" />
                            </div>
              <div>
                <input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Untitled workflow"
                  className={cn(
                    "font-medium text-base sm:text-lg text-gray-900 placeholder-gray-400 bg-transparent border-0",
                    "focus:outline-none focus:ring-0 px-0 py-1 transition-colors w-full",
                    isSidePanelOpen ? "min-w-[200px] max-w-[300px]" : "min-w-[300px] max-w-[500px]",
                    "hover:bg-gray-50/50 focus:bg-gray-50/50 rounded-md px-2",
                    validationErrors.name && "text-red-700 placeholder-red-300"
                  )}
                  style={{ fontFamily: 'system-ui, -apple-system, sans-serif', lineHeight: '1.3' }}
                  disabled={isSubmitting}
                />
                <Select value={formData.category_id} onValueChange={handleCategoryChange}>
                  <SelectTrigger className={cn("h-6 text-[10px] sm:text-xs text-gray-500 border-0 bg-transparent hover:bg-gray-50 focus:bg-gray-50 rounded-md px-2 py-1 mt-0.5", "focus:ring-0 focus:outline-none shadow-none w-full min-w-0", validationErrors.category_id && "text-red-500")}>
                    <SelectValue placeholder="Choose category..." />
                  </SelectTrigger>
                  <SelectContent className="z-[10060] bg-white border border-gray-200 rounded-lg shadow-lg">
                    {categories.map(category => (<SelectItem key={category.id} value={category.id} className="text-sm py-2.5 hover:bg-gray-50 focus:bg-purple-50 cursor-pointer">{category.name}</SelectItem>))}
                    <SelectItem value="new" className="text-sm text-purple-600 py-2.5 hover:bg-purple-50 focus:bg-purple-100 cursor-pointer border-t border-gray-100">
                      <div className="flex items-center">
                        <div className="w-4 h-4 rounded-lg bg-purple-500 flex items-center justify-center mr-2"><Plus className="h-2.5 w-2.5 text-white" /></div> Create new category
                        </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                {validationErrors.category_id && (<div className="text-xs text-red-500 mt-1">{validationErrors.category_id}</div>)}
                {validationErrors.name && (<div className="text-xs text-red-500 mt-1">{validationErrors.name}</div>)}
                      </div>
            </div>
          </div>
          <div className={cn(
            "flex items-center flex-shrink-0 transition-all duration-300",
            isSidePanelOpen ? "gap-2 sm:gap-3" : "gap-4 sm:gap-6"
          )}>
            <div className="text-right hidden sm:block">
              <div className="text-xs sm:text-sm font-medium text-gray-700">{isNew ? 'New Complex Answer' : 'Complex Answer'}</div>
              <div className="text-xs text-gray-500 mt-0.5">Smart conversational flow</div>
            </div>

            <div className="relative" ref={descriptionRef}>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsDescriptionOpen(prev => !prev)}
                className="rounded-full h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                aria-label="Workflow Settings"
              >
                <DotsThreeVertical size={16} />
              </motion.button>
              <AnimatePresence>
              {isDescriptionOpen && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: -10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: -10 }}
                  transition={{ duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
                  className="absolute top-full right-0 mt-2 w-80 bg-white rounded-xl shadow-lg border p-4 z-50"
                >
                  <div className="space-y-2">
                    <Label htmlFor="description" className="text-sm font-medium text-gray-700">
                      Description <span className="text-gray-400 text-xs">(Optional)</span>
                    </Label>
                    <Textarea
                      id="description"
                      name="description"
                      value={formData.description || ''}
                      onChange={handleChange}
                      placeholder="Describe what this workflow accomplishes..."
                      className="min-h-[100px] text-sm border-gray-200 bg-white focus:border-purple-500 focus:ring-purple-500 rounded-xl resize-none"
                    />
                  </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {onOpenActivationConditions && setting && (
               <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onOpenActivationConditions(setting)}
                  className="text-gray-700 border-gray-200 hover:bg-gray-50"
               >
                 <Clock className="w-4 h-4 mr-2" />
                 Conditions
               </Button>
            )}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleSubmit}
              disabled={isSubmitting}
              className={cn("py-2 px-4 sm:px-5 rounded-xl text-xs sm:text-sm font-medium transition-colors", "bg-purple-500 hover:bg-purple-600 text-white shadow-sm", "disabled:opacity-50 disabled:cursor-not-allowed")}
            >
              {isSubmitting ? (
                <div className="flex items-center">
                  <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }} className="mr-1.5 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4 border-2 border-white border-t-transparent rounded-full" />
                  <span className="hidden sm:inline">Saving...</span><span className="sm:hidden">Save</span>
                </div>
              ) : (
                <><span className="hidden sm:inline">Save</span><span className="sm:hidden">Save</span></>
              )}
            </motion.button>
            <button onClick={handleCloseAttempt} className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </motion.div>

      {/* Split-Screen Workflow Builder */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Workflow Steps */}
                          <motion.div 
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2, duration: 0.4 }}
          className={cn(
            "bg-gray-50/50 border-r border-gray-200/50 flex flex-col transition-all duration-300",
            isSidePanelOpen ? "w-60" : "w-80"
          )}
        >
                    <div className={cn(
            "border-b border-gray-200/50 transition-all duration-300",
            isSidePanelOpen ? "p-3" : "p-6"
          )}>
            <h3 className={cn(
              "font-medium text-gray-900 tracking-tight transition-all duration-300",
              isSidePanelOpen ? "text-xs" : "text-sm"
            )}>Answer Steps</h3>
            <p className={cn(
              "text-gray-500 transition-all duration-300",
              isSidePanelOpen ? "text-[10px] mt-0.5" : "text-xs mt-1"
            )}>Build your conversational flow</p>
          </div>
          <div className={cn(
            "flex-1 transition-all duration-300",
            isSidePanelOpen ? "p-2 space-y-1" : "p-4 space-y-2"
          )}>
            {[
              { id: 'triggers', icon: Lightning, label: 'Triggers', description: 'Keywords that activate' },
              { id: 'questions', icon: Question, label: 'Questions', description: 'Collect customer data' },
              { id: 'response', icon: Database, label: 'Response', description: 'Define the output' },
              ...(formData.answer_type === 'api' && formData.questions?.length > 0 ? [{ id: 'test', icon: Play, label: 'Test', description: 'Validate your workflow' }] : [])
            ].map((step) => {
              const StepIcon = step.icon;
              const isActive = currentStep === step.id;
              const isCompleted = isStepCompleted(step.id);
              const isAccessible = isStepAccessible(step.id);
              return (
                <motion.button
                  key={step.id}
                  whileHover={isAccessible ? { scale: 1.02 } : {}}
                  whileTap={isAccessible ? { scale: 0.98 } : {}}
                  onClick={() => isAccessible && setCurrentStep(step.id)}
                  disabled={!isAccessible}
                  className={cn(
                    "w-full flex items-center rounded-xl text-left transition-all duration-200",
                    isSidePanelOpen ? "p-2" : "p-3",
                    isActive && "bg-purple-50 border border-purple-200 shadow-sm",
                    !isActive && isAccessible && "bg-white border border-gray-200 hover:bg-gray-50 hover:shadow-sm",
                    !isAccessible && "bg-gray-100 border border-gray-200 opacity-50 cursor-not-allowed"
                  )}
                >
                  <div className={cn(
                    "flex items-center flex-1 min-w-0 transition-all duration-300",
                    isSidePanelOpen ? "space-x-2" : "space-x-3"
                  )}>
                    <div className={cn(
                      "flex items-center justify-center rounded-lg flex-shrink-0 transition-all duration-300",
                      isSidePanelOpen ? "w-6 h-6" : "w-8 h-8",
                      isCompleted ? "bg-green-100 text-green-600" : isActive ? "bg-purple-100 text-purple-600" : isAccessible ? "bg-gray-100 text-gray-600" : "bg-gray-100 text-gray-400"
                    )}>
                      {isCompleted ? (
                        <Check className={cn("transition-all duration-300", isSidePanelOpen ? "w-3 h-3" : "w-4 h-4")} />
                      ) : (
                        <StepIcon className={cn("transition-all duration-300", isSidePanelOpen ? "w-3 h-3" : "w-4 h-4")} />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={cn(
                        "font-medium transition-all duration-300",
                        isSidePanelOpen ? "text-xs" : "text-sm",
                        isActive ? "text-purple-900" : isAccessible ? "text-gray-900" : "text-gray-500"
                      )}>{step.label}</div>
                      <div className={cn(
                        "truncate transition-all duration-300",
                        isSidePanelOpen ? "text-[10px] mt-0" : "text-xs mt-0.5",
                        isActive ? "text-purple-600" : isAccessible ? "text-gray-500" : "text-gray-400"
                      )}>{step.description}</div>
                    </div>
                    {isActive && (
                      <div className={cn(
                        "bg-purple-500 rounded-full flex-shrink-0 transition-all duration-300",
                        isSidePanelOpen ? "w-1.5 h-1.5" : "w-2 h-2"
                      )} />
                    )}
                  </div>
                </motion.button>
              );
            })}
                    </div>
                  </motion.div>

        {/* Main Canvas - Active Step Content */}
        <div className="flex-1 bg-white overflow-y-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.4 }}
            className={cn(
              "mx-auto transition-all duration-300",
              isSidePanelOpen ? "p-4 sm:p-6 max-w-2xl" : "p-4 sm:p-6 max-w-4xl"
            )}
          >
             {/* Render Active Step Content */}
             {renderStepContent()}
          </motion.div>
            </div>
      </div>
    </motion.div>
  );

  if (isInline) {
    return renderContent();
  }

  return (
    <>
      {/* Modal Backdrop */}
      <motion.div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[10060] flex items-start justify-center overflow-auto pt-[5vh] px-[5vw] pb-[5vh]"
        variants={backdropVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
        onClick={handleCloseAttempt}
      >
        {renderContent()}
      </motion.div> {/* End backdrop */}

      {/* Unsaved Changes Modal */}
      <ConfirmModal
        isOpen={isUnsavedChangesModalOpen}
        onClose={() => setIsUnsavedChangesModalOpen(false)}
        onConfirm={handleCloseConfirmed}
        title="Discard Changes"
        message="You have unsaved changes. Are you sure you want to discard them?"
        confirmText="Discard Changes"
        confirmStyle="bg-red-600 hover:bg-red-700"
        cancelText="Continue Editing"
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        title="Delete Complex Answer"
        message={`Are you sure you want to delete "${formData.name || 'this complex answer'}"? This action cannot be undone.`}
        confirmText="Delete"
        confirmStyle="bg-red-600 hover:bg-red-700 focus:ring-red-500"
        cancelText="Cancel"
      />

    </>
  );
}; 